/* @sravni/react-header version 0.2.43 */
import { __read } from 'tslib';
import * as React from 'react';
import { useStyles, noop } from '@sravni/react-utils';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Accordion } from '@sravni/react-design-system/lib/Accordion';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { Cross, Menu } from '@sravni/react-icons';
import { getCustomStyles } from '../../../utils/styles.js';
import { getCustomIconStyles } from '../../../utils/customIcons.js';
import { makeRelAttribute } from '../../../utils/links.js';
import { defaultMobileNavigation } from '../../../mocks/defaultMobileNavigation.js';
import { useMenuIntersectionObservers } from '../../../hooks/useMenuIntersectionObservers.js';
import { useAnalyticEvents, EVENTS } from '../../../hooks/useAnalyticEvents.js';
import '../../Navigation/index.js';
import '../../InjectedIcon/index.js';
import '../../Search/index.js';
import modules_8de2575d from '../styles/MobileHeader.module.less.js';
import InjectedIcon from '../../InjectedIcon/src/InjectedIcon.js';
import Navigation from '../../Navigation/src/Navigation.js';
import NavigationItem from '../../Navigation/src/NavigationItem.js';
import Search from '../../Search/src/Search.js';

/**
 * @description Mobile navigation component.
 *
 * @component
 * @example
 * ```jsx
 * <MobileNavigation settings={settings} />
 * ```
 */
var MobileNavigation = React.forwardRef(function (props, ref) {
    var _a = props.settings, settings = _a === void 0 ? {} : _a, visible = props.visible, searchUrl = props.searchUrl, promo = props.promo, showSearch = props.showSearch, className = props.className, onOpen = props.onOpen, onClose = props.onClose;
    var cx = useStyles(modules_8de2575d);
    var pushAnalyticEvent = useAnalyticEvents().pushAnalyticEvent;
    var _b = __read(React.useState([]), 2), menuState = _b[0], setMenuState = _b[1];
    var navigation = React.useMemo(function () { return settings.mobileMenu || defaultMobileNavigation; }, [settings]);
    var navigationMap = React.useMemo(function () { return navigation.map(function (o) { return o.text; }).filter(function (o) { return o !== ''; }); }, [navigation]);
    var handleTopMenuClick = React.useCallback(function (indexes) {
        var isExpandAction = indexes.length > menuState.length;
        var label = navigationMap[indexes[indexes.length - 1]];
        setMenuState(indexes);
        isExpandAction && pushAnalyticEvent(EVENTS.TOP_MENU_SHOW, { eventLabel: label + "|\u041A\u043B\u0438\u043A" });
    }, [menuState, navigationMap]);
    var handleLinkClick = React.useCallback(function (event) {
        event.stopPropagation();
        var label = event.currentTarget.dataset.label || '';
        var section = event.currentTarget.dataset.section || '';
        pushAnalyticEvent(EVENTS.MENU_LINK_CLICK, { eventLabel: section + "|" + label });
    }, []);
    var handlePromoBannerClick = React.useCallback(function () {
        pushAnalyticEvent(EVENTS.PROMO_BANNER_CLICK);
    }, []);
    var handlePromoLinkInView = React.useCallback(function (inView, entry) {
        // dataset есть, типизация странная
        var label = entry.target.dataset.label || '';
        var url = entry.target.href || '';
        var section = entry.target.dataset.section || '';
        if (inView && label.length > 0) {
            pushAnalyticEvent(EVENTS.PROMO_LINK_IN_VIEW, { eventLabel: section + "|" + label + "|" + url });
        }
    }, []);
    var promoLinksIntersectionObservedRefs = useMenuIntersectionObservers({
        menu: navigation,
        promoLinksCallback: handlePromoLinkInView,
        topMenuLinksCallback: noop
    }).refs;
    var renderGroups = function (groups, sectionTitle) { return (React.createElement(Space, { size: 16, direction: "vertical" }, groups.map(function (group) { return (React.createElement(Space, { key: group.text, size: 16, direction: "vertical" }, group.children.map(function (config) { return (React.createElement(Link, { ref: promoLinksIntersectionObservedRefs["" + config.text + config.href], "data-label": config.text, "data-section": sectionTitle, color: "black", key: config.href, href: config.href, style: getCustomStyles(config), target: config.openInNew ? '_blank' : undefined, rel: makeRelAttribute(config), onClick: handleLinkClick },
        React.createElement(Space, { align: "center", inline: true, size: 12, className: cx('navMenuItem') },
            !!config.iconSrc && (React.createElement(Icon, { size: 16, color: "gray", style: getCustomIconStyles(config.href) },
                React.createElement(InjectedIcon, { iconSrc: config.iconSrc }))),
            config.icon && !config.iconSrc ? (React.createElement(Icon, { size: 16, color: "gray", style: getCustomIconStyles(config.href) },
                React.createElement("img", { src: config.icon }))) : null,
            React.createElement(Text, { as: "span", strong: true }, config.text)))); }))); }))); };
    var renderTopLink = function (config) {
        return (React.createElement(Accordion.Item, { "data-label": config.text, key: config.text, title: config.text, style: getCustomStyles(config), unmountOnExit: false }, renderGroups(config.children, config.text)));
    };
    var menu = React.useMemo(function () { return navigation.map(renderTopLink); }, [navigation, promoLinksIntersectionObservedRefs]);
    var menuButton = React.useMemo(function () {
        if (visible) {
            return (React.createElement(Icon, { className: cx('burger'), background: "gray", onClick: onClose },
                React.createElement(Cross, null)));
        }
        return (React.createElement(Icon, { className: cx('burger'), background: "gray", onClick: onOpen },
            React.createElement(Menu, null)));
    }, [visible]);
    return (React.createElement(Navigation, { ref: ref, className: cx(className) },
        React.createElement(NavigationItem, { clean: true, wide: true, fullscreen: true, visible: visible, control: menuButton },
            React.createElement(Accordion, { className: cx('accordion'), onChange: handleTopMenuClick }, menu),
            React.createElement("div", { className: "h-ml-16 h-mt-8 h-mb-8", style: { marginRight: '100%' }, onClick: handlePromoBannerClick }, promo),
            showSearch && React.createElement(Search, { url: searchUrl, isMobileHeader: true, visible: false }))));
});
MobileNavigation.defaultProps = {};

export { MobileNavigation as default };
//# sourceMappingURL=MobileNavigation.js.map
